import { useParams, Link, useNavigate } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useToast } from '../hooks/useToast';
import { getContentId } from '../types';
import SectionToggle from '../components/SectionToggle';
import PageSettings from '../components/PageSettings';
import DraggableList from '../components/DraggableList';
import { useEditorPreview } from '../hooks/useEditorPreview';
import '../styles/editor.css';

export default function HoursList() {
  const { siteId } = useParams<{ siteId: string }>();
  const { items, loading, error, remove, reorder } = useContent(siteId, 'hours');
  const { addToast } = useToast();
  const navigate = useNavigate();
  useEditorPreview('/');

  const handleDelete = async (contentKey: string, day: string) => {
    if (!confirm(`Delete hours for "${day}"?`)) return;
    try {
      const id = getContentId(contentKey);
      await remove(id);
      addToast('success', 'Hours entry deleted');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleReorder = async (fromIndex: number, toIndex: number) => {
    const newItems = [...items];
    const [moved] = newItems.splice(fromIndex, 1);
    newItems.splice(toIndex, 0, moved);
    const ordered = newItems.map((item, i) => ({
      id: getContentId(item.contentKey),
      sortOrder: i,
    }));
    try {
      await reorder(ordered);
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to reorder');
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header"><h1>Opening Hours</h1></div>
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-header"><h1>Opening Hours</h1></div>
        <div className="error-state"><p>{error}</p></div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Opening Hours</h1>
        <div className="page-header-actions">
          <SectionToggle siteId={siteId!} field="showHours" label="Opening Hours" />
          <Link to={`/sites/${siteId}/content/hours/new`} className="btn btn-primary">
            + Add Hours
          </Link>
        </div>
      </div>

      <PageSettings siteId={siteId!} fields={['hoursTitle']} />

      {items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon"><svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ccc' }}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></div>
          <h2>No opening hours set</h2>
          <p>Let visitors know when you&apos;re open.</p>
          <p style={{ fontSize: '0.85rem', color: 'var(--color-gray-500)', marginBottom: 16 }}>
            Hours are shown on your contact page.
          </p>
          <Link to={`/sites/${siteId}/content/hours/new`} className="btn btn-primary">
            + Add your hours
          </Link>
        </div>
      ) : (
        <DraggableList
          items={items.map((item) => {
            const data = item.data as Record<string, string>;
            const contentId = getContentId(item.contentKey);
            const isClosed = data.closed === 'true' || data.closed === true as unknown as string;
            return {
              key: item.contentKey,
              element: (
                <>
                  <div
                    className="list-item-content"
                    onClick={() =>
                      navigate(`/sites/${siteId}/content/hours/${contentId}`)
                    }
                  >
                    <h3 className="list-item-title">
                      {data.day || 'Untitled'}
                      {isClosed
                        ? <span className="tag" style={{ marginLeft: '0.5rem', background: '#fee', color: '#c33' }}>Closed</span>
                        : null}
                    </h3>
                    <div className="list-item-meta">
                      {!isClosed && (data.openTime || data.closeTime) && (
                        <span className="list-item-price">
                          {data.openTime || '?'} – {data.closeTime || '?'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="list-item-actions">
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(item.contentKey, data.day || 'this entry')}
                    >
                      Delete
                    </button>
                  </div>
                </>
              ),
            };
          })}
          onReorder={handleReorder}
        />
      )}
    </div>
  );
}
